import { create_control_form_from_row, create_empty_control_form, register_rows, resolve_control_result_class } from './controls_data';

let control_row_seq = register_rows.reduce((max, row) => {
  const numeric = Number(String(row.id || '').replace(/\D/g, ''));
  return Number.isFinite(numeric) ? Math.max(max, numeric) : max;
}, 0);

function next_control_row_id() {
  control_row_seq += 1;
  return `C-${control_row_seq}`;
}

function resolve_modal_index(index, row, register_rows) {
  if (typeof index === 'number') return index;
  if (!row) return null;
  return register_rows.findIndex((item) => item.id === row.id);
}

function value_or_default(value, fallback_value) {
  const resolved_value = value || fallback_value;
  return resolved_value;
}

function normalized_control_form(form) {
  const normalized_form = {
    latest_result: value_or_default(form.latest_result, 'Not tested'),
    control_name: value_or_default(form.control_name.trim(), 'Untitled control'),
    control_subtitle: form.description.trim(),
    category: value_or_default(form.category, 'Finance'),
    owner: value_or_default(form.owner, 'TBC'),
    frequency: value_or_default(form.testing_frequency, 'Monthly'),
    linked_risk: value_or_default(form.objective, 'TBC'),
    next_due: value_or_default(form.next_due, '')
  };
  return normalized_form;
}

function control_row_from_form(form, row_id) {
  const normalized_form = normalized_control_form(form);
  const control_row = {
    id: row_id,
    control: normalized_form.control_name,
    control_subtitle: normalized_form.control_subtitle,
    category: normalized_form.category,
    owner: normalized_form.owner,
    frequency: normalized_form.frequency,
    linked_risk: normalized_form.linked_risk,
    result: { label: normalized_form.latest_result, class: resolve_control_result_class(normalized_form.latest_result) },
    next_due: normalized_form.next_due,
    highlighted: true
  };
  return control_row;
}

export const controls_methods = {
  set_tab(tab_id) {
    this.active_tab = tab_id;
  },
  drawer_tone_class(tone) {
    const tone_map = { teal: 'is-green', green: 'is-green', amber: 'is-orange', orange: 'is-orange', red: 'is-red', slate: 'is-slate', document: 'is-slate', report: 'is-slate', approval: 'is-green', certificate: 'is-green', log: 'is-slate' };
    const tone_class = Reflect.get(tone_map, tone) || 'is-slate';
    return tone_class;
  },
  drawer_chip_class(kind) {
    return this.drawer_tone_class(kind);
  },
  drawer_icon_for_type(type) {
    const icon_map = { Obligation: 'bi-file-earmark-check', Supplier: 'bi-truck', Finance: 'bi-cash-coin', Procurement: 'bi-cart', Contract: 'bi-file-earmark-text', 'Evidence vault': 'bi-archive', Audit: 'bi-clipboard-check', Reports: 'bi-file-earmark-bar-graph', 'Risk register': 'bi-exclamation-triangle', 'No incident': 'bi-shield-check', 'Corrective action': 'bi-arrow-repeat', Remediation: 'bi-wrench-adjustable-circle', Governance: 'bi-diagram-3', Privacy: 'bi-shield-lock', Employee: 'bi-people', Training: 'bi-mortarboard', System: 'bi-pc-display-horizontal', Legal: 'bi-briefcase' };
    return Reflect.get(icon_map, type) || 'bi-circle';
  },
  sync_overlay_state() {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('controls-library-overlay-open', this.control_modal_open || Boolean(this.active_control_drawer_key) || Boolean(this.active_remediation_drawer_key));
  },
  open_control_drawer(row_or_id = null) {
    const row_id = typeof row_or_id === 'string' ? row_or_id : row_or_id?.id || null;
    if (!row_id) return;
    this.close_control_modal();
    this.active_remediation_drawer_key = null;
    this.active_control_drawer_key = row_id;
    this.sync_overlay_state();
  },
  open_remediation_drawer(row = null) {
    const row_id = row?.id || null;
    if (!row_id) return;
    this.close_control_modal();
    this.active_control_drawer_key = null;
    this.active_remediation_drawer_key = row_id;
    this.sync_overlay_state();
  },
  close_control_drawer() {
    this.active_control_drawer_key = null;
    this.active_remediation_drawer_key = null;
    this.sync_overlay_state();
  },
  open_control_modal(row = null, index = null) {
    const resolved_index = resolve_modal_index(index, row, this.register_rows);
    this.control_modal_mode = row ? 'edit' : 'create';
    this.control_modal_index = resolved_index;
    this.control_modal_form = row ? create_control_form_from_row(row) : create_empty_control_form();
    this.close_control_drawer();
    this.control_modal_open = true;
    this.sync_overlay_state();
  },
  close_control_modal() {
    this.control_modal_open = false;
    this.control_modal_mode = 'create';
    this.control_modal_index = null;
    this.sync_overlay_state();
  },

  get_control_existing_row(index) {
    if (index === null || index === undefined) return null;
    return Reflect.get(this.register_rows, index) || null;
  },

  is_control_edit_mode() {
    return this.control_modal_mode === 'edit' && this.control_modal_index !== null;
  },

  get_control_row_id(existing_row) {
    return this.is_control_edit_mode() && existing_row
      ? existing_row.id
      : next_control_row_id();
  },

  apply_control_row_save(row, existing_row) {
    if (this.is_control_edit_mode() && existing_row) {
      const updated_row = { ...existing_row, ...row };
      this.$set(this.register_rows, this.control_modal_index, updated_row);
    } else {
      this.register_rows.unshift(row);
    }
  },

  save_control_modal() {
    const form = this.control_modal_form || create_empty_control_form();

    const existing_row = this.get_control_existing_row(this.control_modal_index);
    const row_id = this.get_control_row_id(existing_row);

    const row = control_row_from_form(form, row_id);

    this.apply_control_row_save(row, existing_row);

    this.close_control_modal();
  }
};

export const controls_hooks = {
  mounted() {
    this.sync_overlay_state();
  },
  beforeDestroy() {
    if (typeof document !== 'undefined') document.body.classList.remove('controls-library-overlay-open');
  }
};

export const controls_watch = {
  control_modal_open() {
    this.sync_overlay_state();
  },
  active_control_drawer_key() {
    this.sync_overlay_state();
  },
  active_remediation_drawer_key() {
    this.sync_overlay_state();
  }
};
